import { UseQuery } from "@reduxjs/toolkit/dist/query/react/buildHooks";
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { QueryDefinition } from "@reduxjs/toolkit/query";
import { isEqual } from "lodash";

const DEFAULT_LIMIT = 5;
type QDef<QueryArg, ResultType> = QueryDefinition<
  QueryArg & { offset?: number; limit?: number },
  any,
  string,
  ResultType,
  string
>;

type UsePaginationProps<QueryArg, ResultType> = {
  useQuery: UseQuery<
    QDef<QueryArg & { offset?: number; limit?: number }, ResultType[]>
  >;
  params: QueryArg;
  limit?: number;
  options?: any;
  withTransition?: boolean;
  formatDataChunkFn?: (data: any) => ResultType[];
  getTotalDataFn?: (data: any) => any;
  totalParams?: Record<string, any>;
};

export default function usePagination<QueryArg, ResultType>({
  useQuery,
  limit = DEFAULT_LIMIT,
  params,
  options,
  withTransition,
  formatDataChunkFn,
  getTotalDataFn,
  totalParams
}: UsePaginationProps<QueryArg, ResultType>) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [newOffset, setNewOffset] = useState<number | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [data, setData] = useState<ResultType[]>([]);
  const [currentParams, setCurrentParams] = useState<QueryArg>(params);
  const [currentOptions, setCurrentOptions] = useState<any>(options);
  const [refreshKey, setRefreshKey] = useState(0);
  const disabledFetchingRef = useRef<boolean>(false);
  const [totalData, setTotalData] = useState<any>();

  const {
    data: rawDataChunk,
    refetch,
    isFetching,
    isLoading: isInitialLoading,
    isSuccess,
    isError,
    isUninitialized,
    originalArgs
  } = useQuery(
    {
      ...currentParams,
      ...(offset === 0 ? totalParams : {}),
      offset,
      limit,
      refreshKey
    },
    {
      ...currentOptions,
      // Always use the fresh skip value — currentOptions lags by 1 render
      // due to the useEffect sync, which can cause unwanted fetches.
      ...(options?.skip !== undefined && { skip: options.skip }),
      signal: abortControllerRef.current?.signal
    }
  );

  const abortRequests = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, []);

  useEffect(() => {
    if (options?.skip) return;
    abortRequests();
  }, [params, options, abortRequests]);

  useEffect(() => {
    if (getTotalDataFn && rawDataChunk && offset === 0) {
      setTotalData(getTotalDataFn(rawDataChunk));
    }
  }, [rawDataChunk, offset, getTotalDataFn]);

  const getDataChunk = useCallback(
    () =>
      formatDataChunkFn
        ? formatDataChunkFn(rawDataChunk)
        : (rawDataChunk ?? []),
    [formatDataChunkFn, rawDataChunk]
  );

  const isLoading = useMemo(
    () =>
      Boolean(
        !options?.skip && (isFetching || isInitialLoading || isUninitialized)
      ),
    [isFetching, isInitialLoading, isUninitialized, options?.skip]
  );
  const hasBeenInitiallyLoaded = !isInitialLoading && !isUninitialized;
  const clearData = useCallback(() => {
    setData([]);
    setOffset(0);
    setHasMoreData(true);
    setNewOffset(undefined);
  }, []);

  const fetchMore = useCallback(() => {
    if (hasMoreData && !isLoading) {
      setTimeout(() => {
        setNewOffset(offset + limit);
      }, 1);
    }
  }, [isLoading, offset, limit, hasMoreData]);

  const onScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      const isScrollable = scrollHeight > clientHeight;
      if (
        hasMoreData &&
        isScrollable &&
        !disabledFetchingRef.current &&
        scrollTop < scrollHeight / 2
      ) {
        disabledFetchingRef.current = true;
        fetchMore();
      }
    },
    [hasMoreData, fetchMore]
  );

  useEffect(() => {
    if (data.length > 0) {
      disabledFetchingRef.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (newOffset !== undefined && newOffset !== offset) {
      const updateFn = () => {
        if (newOffset === 0) {
          clearData();
        } else {
          setOffset(newOffset);
        }
      };
      if (withTransition) {
        requestAnimationFrame(() => {
          updateFn();
        });
      } else {
        updateFn();
      }
    }
  }, [offset, newOffset, clearData, withTransition]);

  useEffect(() => {
    if (!isEqual(params, currentParams) || !isEqual(options, currentOptions)) {
      // Update currentParams/currentOptions even when skip is true, so they're ready
      // when skip becomes false. The skip option in useQuery prevents execution.
      if (options?.skip) {
        setCurrentParams(params);
        setCurrentOptions(options);
        return;
      }
      clearData();
      setCurrentParams(params);
      setCurrentOptions(options);
    }
  }, [params, currentParams, clearData, options, currentOptions]);

  useEffect(() => {
    const dataChunk = getDataChunk();
    if (isLoading || !dataChunk) return;

    // At page 0, always process: handles both initial load and cache-invalidation re-fetches.
    // hasMoreData guard only applies when paginating beyond page 0.
    if (offset !== 0 && !hasMoreData) return;

    // If chunk length < limit, we're done paginating
    if (dataChunk.length < limit) {
      setHasMoreData(false);
    }

    setData((prev) => {
      // Re-fetch at page 0 with existing data (cache invalidation) — replace, don't append
      if (offset === 0 && prev.length > 0) {
        return dataChunk;
      }

      const existingIds = new Set(prev.map((item: any) => item.id));
      const uniqueNewData = dataChunk.filter(
        (item: any) => !existingIds.has(item.id)
      );

      // Only stop pagination if we already have data AND got no new unique items
      // This prevents infinite loops when API returns duplicates
      if (prev.length > 0 && uniqueNewData.length === 0) {
        setHasMoreData(false);
      }

      return [...prev, ...uniqueNewData];
    });
  }, [limit, isLoading, hasMoreData, getDataChunk, offset]);

  const refreshData = useCallback(() => {
    clearData();
    setRefreshKey((prev) => prev + 1);
  }, [clearData]);

  const updateItem = useCallback((id: any, updates: Partial<ResultType>) => {
    setData((prev) =>
      prev.map((item: any) => (item.id === id ? { ...item, ...updates } : item))
    );
  }, []);

  return {
    refetch,
    refreshData,
    isFetching,
    data,
    totalData,
    isLoading,
    isSuccess,
    isError,
    isUninitialized,
    hasBeenInitiallyLoaded,
    hasMoreData,
    fetchMore,
    clearData,
    onScroll,
    offset,
    originalArgs,
    updateItem,
    abortRequests
  };
}
